import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaFileAlt, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import OfferService, {
  OfferTemplateListItem,
  OfferTemplateDetail,
} from '../services/OfferService';

interface CreateOfferFromTemplateModalProps {
  isOpen: boolean;
  partnerId: number;
  projectId?: number;
  onClose: () => void;
  onCreated?: (offerId: number) => void;
}

export default function CreateOfferFromTemplateModal({
  isOpen,
  partnerId,
  projectId,
  onClose,
  onCreated,
}: CreateOfferFromTemplateModalProps): React.JSX.Element | null {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<OfferTemplateListItem[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templateDetail, setTemplateDetail] = useState<OfferTemplateDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
      setSelectedTemplateId(null);
      setTemplateDetail(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedTemplateId) {
      loadTemplateDetail(selectedTemplateId);
    } else {
      setTemplateDetail(null);
    }
  }, [selectedTemplateId]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await OfferService.fetchOfferTemplates();
      // Filter only active templates
      setTemplates(data.filter((t) => t.is_active));
    } catch (err) {
      console.error('Failed to load templates:', err);
      toast.error('Vorlagen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateDetail = async (templateId: number) => {
    setLoadingDetail(true);
    try {
      const data = await OfferService.fetchOfferTemplate(templateId);
      setTemplateDetail(data);
    } catch (err) {
      console.error('Failed to load template detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedTemplateId) {
      toast.error('Bitte eine Vorlage auswählen');
      return;
    }

    setCreating(true);
    try {
      const result = await OfferService.createOfferFromTemplate({
        partner_id: partnerId,
        project_id: projectId,
        template_id: selectedTemplateId,
      });

      toast.success('Angebot aus Vorlage erstellt');

      if (onCreated) {
        onCreated(result.offer_id);
      }

      // Navigate to the offer editor
      navigate(`/offers/${result.offer_id}/edit`);
      onClose();
    } catch (err: any) {
      console.error('Failed to create offer from template:', err);

      if (err?.response?.status === 409) {
        toast.warning('Vorlage ist deaktiviert');
      } else if (err?.response?.status === 404) {
        toast.error('Projekt oder Vorlage nicht gefunden');
      } else {
        const message = err?.response?.data?.detail || 'Konnte Angebot nicht erstellen';
        toast.error(message);
      }
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  const getTradeColor = (trade: string): { bg: string; text: string } => {
    switch (trade?.toUpperCase()) {
      case 'HEIZUNG':
        return { bg: '#fee2e2', text: '#991b1b' };
      case 'SANITÄR':
      case 'SANITAR':
        return { bg: '#dbeafe', text: '#1e40af' };
      case 'SOLAR':
        return { bg: '#fef3c7', text: '#92400e' };
      case 'KLIMA':
        return { bg: '#d1fae5', text: '#065f46' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  return (
    <div
      data-testid="offer-template-modal"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #e0e3ef',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FaFileAlt size={20} color="#021a4a" />
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
              Angebot aus Vorlage
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#666',
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <FaSpinner className="spin" size={24} color="#666" />
            </div>
          ) : templates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Keine Vorlagen verfügbar
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '24px' }}>
              {/* Template List */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#666', marginBottom: '12px' }}>
                  Vorlage auswählen
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {templates.map((template) => {
                    const tradeColor = getTradeColor(template.trade);
                    const isSelected = selectedTemplateId === template.id;

                    return (
                      <div
                        key={template.id}
                        data-testid={`offer-template-card-${template.id}`}
                        onClick={() => setSelectedTemplateId(template.id)}
                        style={{
                          padding: '14px 16px',
                          border: isSelected ? '2px solid #021a4a' : '1px solid #e0e3ef',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          background: isSelected ? '#f0f4ff' : 'white',
                          transition: 'all 0.15s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                          {isSelected && <FaCheckCircle size={14} color="#021a4a" />}
                          <span style={{ fontWeight: 600, color: '#021a4a', flex: 1 }}>
                            {template.name}
                          </span>
                          <span
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 600,
                              background: tradeColor.bg,
                              color: tradeColor.text,
                            }}
                          >
                            {template.trade}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {template.milestones_count} Meilensteine · {template.lines_count} Positionen
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Preview Panel */}
              {selectedTemplateId && (
                <div style={{ flex: 1, borderLeft: '1px solid #e0e3ef', paddingLeft: '24px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#666', marginBottom: '12px' }}>
                    Vorschau
                  </div>

                  {loadingDetail ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                      <FaSpinner className="spin" size={18} color="#666" />
                    </div>
                  ) : templateDetail ? (
                    <div>
                      {/* Description */}
                      {templateDetail.description && (
                        <div style={{ marginBottom: '16px', fontSize: '13px', color: '#666' }}>
                          {templateDetail.description}
                        </div>
                      )}

                      {/* Milestones */}
                      {templateDetail.milestones.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#888', marginBottom: '8px' }}>
                            Zahlungsplan
                          </div>
                          {templateDetail.milestones.map((milestone, idx) => (
                            <div
                              key={idx}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '6px 0',
                                borderBottom: '1px solid #f0f0f0',
                                fontSize: '12px',
                              }}
                            >
                              <span style={{ color: '#333' }}>{milestone.label}</span>
                              <span style={{ color: '#666', fontWeight: 500 }}>{milestone.percentage}%</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Lines */}
                      {templateDetail.lines.length > 0 && (
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#888', marginBottom: '8px' }}>
                            Positionen
                          </div>
                          {templateDetail.lines.slice(0, 5).map((line, idx) => (
                            <div
                              key={idx}
                              style={{
                                padding: '6px 0',
                                borderBottom: '1px solid #f0f0f0',
                                fontSize: '12px',
                                color: '#333',
                              }}
                            >
                              {line.name}
                            </div>
                          ))}
                          {templateDetail.lines.length > 5 && (
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>
                              + {templateDetail.lines.length - 5} weitere Positionen
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            padding: '16px 24px',
            borderTop: '1px solid #e0e3ef',
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: '1px solid #e0e3ef',
              borderRadius: '10px',
              background: 'white',
              color: '#666',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Abbrechen
          </button>
          <button
            data-testid="offer-template-create-btn"
            onClick={handleCreate}
            disabled={!selectedTemplateId || creating}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '10px',
              background: selectedTemplateId ? '#021a4a' : '#ccc',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              cursor: selectedTemplateId ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {creating ? (
              <>
                <FaSpinner className="spin" size={14} />
                Erstelle...
              </>
            ) : (
              'Angebot erstellen'
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
}
